// La page imprimée d'une ABS (/etudes/:id/impression, CDC du 14/10/2026, § 7.5 et § 8) : celle que
// le Chromium du backend imprime en PDF. A4 portrait, une partie par page, en-tête et pied de page
// répétés, bandeau « Document de travail » tant que l'étude n'est ni validée ni livrée, et les blocs
// « à compléter par le CCAS » restés vides regroupés en annexe. Aucune commande d'édition.
//
// Auth : `?pdf_token=<jeton court, confiné au document>`, passé en Authorization sur l'appel à
// l'API, comme la page imprimable de l'observatoire (obs/PrintView). Pas de session : le Chromium
// n'en a pas. `data-print-ready` / `data-print-failed` disent au backend où en est la page.
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Logo from '../../Logo'
import { DOMAINE_PUBLIC, NOM_PRODUIT } from '../../constants'
import DocumentAbs, { aCompleterVide, sectionsVisibles } from './DocumentAbs'
import { lireEtude, type Etude } from './api'
import { dateLongue } from './format'
import type { BlocACompleter, Document } from './types'
import '../../obs/obs.css'
import './abs.css'

export const TITRE_ANNEXE_CCAS = 'Données à recueillir auprès du CCAS'
export const BANDEAU_TRAVAIL = 'Document de travail'

/** Les blocs à compléter restés vides des sections imprimées, regroupés par section. */
export function donneesARecueillir(doc: Document): { section: string; blocs: BlocACompleter[] }[] {
  return sectionsVisibles(doc, 'impression')
    .map(s => ({ section: s.titre, blocs: (s.blocs ?? []).filter(aCompleterVide) }))
    .filter(g => g.blocs.length > 0)
}

export default function ImpressionAbs() {
  const { id = '' } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const jeton = params.get('pdf_token')
  const [etude, setEtude] = useState<Etude | null>(null)
  const [echec, setEchec] = useState(false)

  useEffect(() => {
    if (!jeton) return
    let vivant = true
    lireEtude(id, jeton)
      .then(e => { if (vivant) { if (e.document) setEtude(e); else setEchec(true) } })
      // Échec = une page d'une ligne : la garde du backend (nombre de pages) refuse ce PDF.
      .catch(() => { if (vivant) setEchec(true) })
    return () => { vivant = false }
  }, [id, jeton])

  if (!jeton) return <p>Jeton manquant : ce document se génère depuis le bouton « Télécharger le PDF ».</p>
  if (echec) return <p data-print-failed="1">Échec d’assemblage de l’étude.</p>
  if (!etude || !etude.document) return <p>Assemblage de l’étude…</p>

  const doc = etude.document
  const travail = etude.statut !== 'valide' && etude.statut !== 'livre'
  const sections = sectionsVisibles(doc, 'impression')
  const annexe = donneesARecueillir(doc)
  const date = dateLongue(new Date().toISOString())

  return (
    <div className="obs2-jetons abs-impression" data-print-ready="1">
      <div className="abs-imp-entete" aria-hidden="true">
        <span>{NOM_PRODUIT} · Analyse des besoins sociaux · {doc.commune}</span>
        {travail && <span>{BANDEAU_TRAVAIL}</span>}
      </div>

      <section className="abs-imp-garde">
        <Logo animation={false} />
        {travail && <p className="abs-imp-bandeau">{BANDEAU_TRAVAIL}</p>}
        <p className="abs-imp-garde-kicker">Analyse des besoins sociaux</p>
        <h1>{doc.commune}</h1>
        <p className="abs-imp-garde-meta">Code commune {doc.insee}</p>
        <p className="abs-imp-garde-meta">
          {doc.donnees_au ? `Données au ${dateLongue(doc.donnees_au)} · ` : ''}version {etude.version} du {dateLongue(etude.modifie_le ?? doc.genere_le)}
        </p>
        <p className="abs-imp-garde-meta">Cadre : article R. 123-1 du code de l’action sociale et des familles.</p>
      </section>

      <section className="abs-imp-page">
        <h2 className="abs-h2">Sommaire</h2>
        <ol className="abs-imp-sommaire">
          {sections.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.titre}</a></li>)}
          {annexe.length > 0 && <li><a href="#annexe-ccas">{TITRE_ANNEXE_CCAS}</a></li>}
        </ol>
      </section>

      <DocumentAbs doc={doc} mode="impression" documentDeTravail={travail} />

      {annexe.length > 0 && (
        <section className="abs-section" id="annexe-ccas">
          <header className="abs-section-tete"><h2 className="abs-h2">{TITRE_ANNEXE_CCAS}</h2></header>
          <p className="abs-md">Ces informations ne figurent dans aucune base publique : seuls le CCAS et ses partenaires les détiennent.
            Elles complètent l’analyse une fois recueillies, avec leur source.</p>
          {annexe.map(g => (
            <div key={g.section}>
              <p className="abs-imp-annexe-section">{g.section}</p>
              {g.blocs.map(b => (
                <div key={b.id} className="abs-bloc abs-ccas">
                  <p><strong>{b.libelle}</strong></p>
                  <p className="abs-ccas-consigne">{b.consigne}</p>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      <div className="abs-imp-pied" aria-hidden="true">
        <span>{NOM_PRODUIT} · {DOMAINE_PUBLIC} · imprimé le {date}</span>
        <span>{travail ? BANDEAU_TRAVAIL : `Version ${etude.version}`}</span>
      </div>
    </div>
  )
}
